import React from "react";
import { Link } from "react-router-dom";
import { navigateToHomeSection } from '../utils/navigation';
import CaseStudyResources from "../components/CaseStudyResources";
import "./creditrisk.css";

const navLinks = ["Services", "Products", "Industries", "Partners", "About", "How We Work", "Case Studies"];
const footerLinks = ["Services", "Products", "Industries", "Partners", "About", "How We Work", "Case Studies", "Contact Us"];
const footerLinkPaths = { "Services": "/#services", "Products": "/#products", "Industries": "/#industries", "Partners": "/#clients", "About": "/#about", "How We Work": "/#how-we-work", "Case Studies": "/#case-studies", "Contact Us": "/#contact" };

const sidebarMeta = [
  { label: "INDUSTRY", value: "Financial Services" },
  { label: "ENGAGEMENT", value: "Strategy · Build · Run" },
  { label: "CAPABILITIES", value: "Machine Learning · Data Platform · MLOps" },
];

const contents = [
  "01 — The Challenge",
  "02 — Our approach",
  "03 — The result",
];

const approachSteps = [
  "Deployed aivionScore, calibrated to the lender's historical portfolio and risk appetite.",
  "Automated extraction of statutory and management accounts with explainable scoring.",
  "Integrated into origination workflows with role-based dashboards for analysts and committee.",
  "Independent model validation and ongoing monitoring framework aligned to MRM standards.",
];

const resultStats = [
  { value: "65%", label: "FASTER CREDIT DECISIONS" },
  { value: "40%", label: "REDUCTION IN MANUAL REVIEW" },
  { value: "22%", label: "IMPROVED RISK ACCURACY" },
];

const moreCaseStudies = [
  { tag: "EDUCATION", title: "Automated Exam Assessment at Scale", img: "education", link: "/exam-assessment" },
  { tag: "HEALTHCARE", title: "Clinical Data Platform for Patient Outcomes", img: "healthcare", link: "/blood-bank-management" },
  { tag: "HEALTHCARE", title: "Elective Recovery and Theatre Optimisation", img: "healthcare", link: "/theatre-optimisation" },
];

export default function CreditRisk() {
  return (
    <div className="cs-root">

      {/* ── DARK HEADER SECTION ── */}
      <header className="cs-header">
        <nav className="cs-nav">
          <div className="cs-nav-inner">
            <div className="cs-logo">
              <span className="cs-logo-aiv">AIV</span><span className="cs-logo-ion">ION</span>
            </div>
            <ul className="cs-nav-links">
              {navLinks.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <button className="cs-contact-btn">CONTACT US</button>
          </div>
        </nav>

        <div className="cs-header-inner">
          <div className="cs-header-left">
            <a href="/#case-studies" className="cs-back-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>← ALL CASE STUDIES</a>
            <p className="cs-tagline">
              CASE STUDY &nbsp;·&nbsp; <span className="cs-tagline-accent">FINANCIAL SERVICES</span>
            </p>
            <h1 className="cs-title">AI-Powered Credit Risk Assessment</h1>
            <p className="cs-summary">
              A leading UK lender struggled with slow, manual credit review processes that
              created bottlenecks and inconsistent risk evaluations across their portfolio.
            </p>
          </div>
          <div className="cs-header-right">
            <p className="cs-outcome-label">HEADLINE OUTCOME</p>
            <p className="cs-outcome-big">65%</p>
            <p className="cs-outcome-sub">Faster credit decisions</p>
            <div className="cs-outcome-divider" />
            <div className="cs-outcome-mini-row">
              <div>
                <p className="cs-mini-val">40%</p>
                <p className="cs-mini-label">Reduction in manual review</p>
              </div>
              <div>
                <p className="cs-mini-val">22%</p>
                <p className="cs-mini-label">Improved risk accuracy</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── HERO IMAGE BANNER ── */}
      <div className="cs-hero-img-wrap">
        <div className="cs-herofin-img" />
      </div>

      {/* ── MAIN CONTENT: SIDEBAR + ARTICLE ── */}
      <section className="cs-main">
        <div className="cs-main-inner">
          {/* sidebar */}
          <aside className="cs-sidebar">
            {sidebarMeta.map((m) => (
              <div key={m.label} className="cs-sidebar-block">
                <p className="cs-sidebar-label">{m.label}</p>
                <p className="cs-sidebar-value">{m.value}</p>
              </div>
            ))}
            <div className="cs-sidebar-block">
              <p className="cs-sidebar-label">CONTENTS</p>
              <ul className="cs-toc">
                {contents.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
          </aside>

          {/* article */}
          <article className="cs-article">
            <h2 className="cs-h2"><span className="cs-h2-num">01</span> The challenge</h2>
            <p className="cs-p">
              Credit analysts were spending the majority of each deal cycle keying numbers from PDF
              accounts, hand-computing covenants and writing memos. Throughput was capped, decisions
              varied between analysts, and the credit committee lacked a real-time view of portfolio risk.
            </p>

            <blockquote className="cs-quote-box">
              <span className="cs-quote-mark">&#8220;</span>
              <p>
                Deployed aivionScore — an automated credit analysis platform using ML-driven risk scoring,
                document extraction, and real-time portfolio dashboards.
              </p>
              <cite>AIVION — ENGAGEMENT SUMMARY</cite>
            </blockquote>

            <h2 className="cs-h2"><span className="cs-h2-num">02</span> Our approach</h2>
            <div className="cs-approach-list">
              {approachSteps.map((step, i) => (
                <div key={i} className="cs-approach-row">
                  <span className="cs-approach-num">{String(i + 1).padStart(2, "0")}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>

            <h2 className="cs-h2"><span className="cs-h2-num">03</span> The result</h2>
            <p className="cs-p">
              Within six months, average time-to-decision dropped by two thirds, manual review effort
              fell sharply, and the credit committee gained a live portfolio view that surfaced
              concentration and emerging risks long before they became losses.
            </p>

            <div className="cs-result-grid">
              {resultStats.map((s) => (
                <div key={s.label} className="cs-result-card">
                  <p className="cs-result-value">{s.value}</p>
                  <p className="cs-result-label">{s.label}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      {/* ── MORE CASE STUDIES ── */}
      <section className="cs-more">
        <div className="cs-more-inner">
          <div className="cs-more-header">
            <div>
              <p className="cs-eyebrow">CONTINUE READING</p>
              <h2 className="cs-more-title">More case studies</h2>
            </div>
            <a href="/#case-studies" className="cs-more-all" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>View all →</a>
          </div>
          <div className="cs-more-grid">
            {moreCaseStudies.map((c) => (
              <div key={c.title} className="cs-more-card">
                <div className={`cs-more-img cs-img-${c.img}`} />
                <p className="cs-more-tag">{c.tag}</p>
                <h4 className="cs-more-card-title">{c.title}</h4>
                <Link to={c.link} className="cs-more-read">READ STUDY &#8594;</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER (dark, striped) ── */}
      <CaseStudyResources title="AI-Powered Credit Risk Assessment" />
      <section className="cs-cta">
        <div className="cs-cta-inner">
          <div className="cs-cta-left">
            <p className="cs-eyebrow">BE OUR NEXT SUCCESS STORY</p>
            <h2 className="cs-cta-title">Let's deliver outcomes like these for your organisation.</h2>
          </div>
          <div className="cs-cta-right">
            <button className="cs-cta-btn-solid" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>START A CONVERSATION &#8594;</button>
            <Link to="/financial-services" className="cs-cta-btn-outline">EXPLORE FINANCIAL SERVICES</Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="cs-footer">
        <div className="cs-footer-inner">
          <div className="cs-footer-brand">
            <div className="cs-footer-logo">
              <span className="cs-logo-aiv">AIV</span><span className="cs-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="cs-footer-icons">
              <span className="cs-icon">in</span>
              <span className="cs-icon">&#9993;</span>
            </div>
          </div>
          <div className="cs-footer-col">
            <p className="cs-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l} href={footerLinkPaths[l]} className="cs-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(footerLinkPaths[l]);}}>{l}</a>)}
            </div>
          </div>
        </div>
        <div className="cs-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
